import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import SportsSectionContent from './SportsSectionContent';
import { mapOverviewSportToTeaserStat } from '../../../utils/ActivityStatsUtils';
import { PlayerProfileTabIdentifiers } from '../../../utils/constants';
import { trackEvent } from '../../Analytics/TrackedClick';
import { profileAnalyticsEvents, getAnalyticsCommonTraits } from '../../../utils/analytics';
import paths from '../../../../constants/paths';

import styles from './activityStatsTeaser.scss';

const badgeClassMap = {
    tennis: styles.badgeTennis,
    pickleball: styles.badgePickleball,
};

function ActivityStatsTeaser({ playerId, isOwnProfile }) {
    const auth = useSelector((state) => state.auth);
    const volumetricStatsOverview = useSelector((state) => state.profile?.volumetricStatsOverview);

    const statsOverviewData = volumetricStatsOverview?.data;
    const loading = !!playerId && volumetricStatsOverview?.isLoading && !statsOverviewData;

    const sportStats = useMemo(() => {
        const sports = statsOverviewData?.sports;
        if (!Array.isArray(sports) || sports.length === 0) { return []; }
        return sports
            .map((sport) => {
                const stat = mapOverviewSportToTeaserStat(sport);
                return stat ? { ...stat, sportKey: sport.sportKey } : null;
            })
            .filter(Boolean);
    }, [statsOverviewData]);

    const hasTeaserData = sportStats.length > 0;

    const statsTabHref = `/profiles/${playerId}?t=${PlayerProfileTabIdentifiers.STATS}&view=activity`;

    const trackCtaClick = (ctaLabel, ctaUrl) => {
        trackEvent(profileAnalyticsEvents.EVENT_CTA_CLICKED, {
            ctaLabel,
            ctaUrl,
            section: 'activity_teaser',
            ...getAnalyticsCommonTraits(auth),
        });
    };

    if (!playerId || loading) { return null; }

    if (!hasTeaserData) {
        return (
            <div className={styles.teaser}>
                <div className={styles.headerRow}>
                    <h6 className={`${styles.title} text-semi`}>Activity History</h6>
                </div>
                <div className={styles.emptyCard}>
                    <div className={styles.emptyCardHeaderBar} />
                    <div className={styles.emptyCardBody}>
                        <p className={styles.emptyMessage}>
                            {isOwnProfile
                                ? 'You have no activity history.'
                                : 'There is no activity history.'}
                        </p>
                        {isOwnProfile && (
                            <div className={styles.emptyLinks}>
                                <Link
                                    className={styles.emptyLink}
                                    to="/search?type=events"
                                    onClick={() => trackCtaClick('Find Events', '/search?type=events')}
                                >
                                    Find Events
                                </Link>
                                <span className={styles.linkSep}>|</span>
                                <Link
                                    className={styles.emptyLink}
                                    to="/search?type=players"
                                    onClick={() => trackCtaClick('Find Players', '/search?type=players')}
                                >
                                    Find Players
                                </Link>
                                <span className={styles.linkSep}>|</span>
                                <Link
                                    className={styles.emptyLink}
                                    to={paths.postScoreSelect}
                                    onClick={() => trackCtaClick('Post Scores', paths.postScoreSelect)}
                                >
                                    Post Scores
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.teaser}>
            <div className={styles.headerRow}>
                <h6 className={`${styles.title} text-semi`}>Activity Stats</h6>
                <Link
                    className={styles.viewAll}
                    to={statsTabHref}
                    onClick={() => trackCtaClick('View all', statsTabHref)}
                >
                    View all
                </Link>
            </div>
            <div className={styles.cardShell}>
                {sportStats.map((stat, index) => (
                    <SportsSectionContent
                        key={stat.sportKey}
                        sportKey={stat.sportKey}
                        badgeClassName={badgeClassMap[stat.sportKey]}
                        stat={stat}
                        showMatchesPlayedHeader={index === 0}
                        matchesPlayedHeader={statsOverviewData?.header}
                    />
                ))}
            </div>
        </div>
    );
}

ActivityStatsTeaser.propTypes = {
    playerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isOwnProfile: PropTypes.bool,
};

ActivityStatsTeaser.defaultProps = {
    playerId: null,
    isOwnProfile: false,
};

export default ActivityStatsTeaser;
